/**
 * Load-benchmarks button (v1.5.0): one click fetches and locally caches the
 * benchmark index proxies (S&P 500, Nasdaq 100) via the free providers, so
 * backtest comparisons have data. Uses the same ingestion path as ticker sync.
 */

import React, { useState } from "react";
import { apiClient } from "@/api/client";
import type { IngestionReport } from "@/types";

interface BenchmarkResult {
  benchmarks: IngestionReport[];
  count: number;
  available: number;
}

export default function LoadBenchmarksButton(): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await apiClient.post<BenchmarkResult>("/providers/sync-benchmarks");
      setMessage(`Loaded ${data.available}/${data.count} benchmarks (S&P 500, Nasdaq 100).`);
    } catch {
      setMessage("Couldn't load benchmark data — check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-mist-100">Benchmark data</p>
        <p className="text-xs text-mist-400">
          Fetch S&amp;P 500 and Nasdaq 100 history so comparisons work.
          {message && <span className="ml-1 text-mist-300">{message}</span>}
        </p>
      </div>
      <button className="btn-secondary shrink-0" disabled={busy} onClick={() => void load()}>
        {busy ? "Loading…" : "Load benchmarks"}
      </button>
    </div>
  );
}
